<template>
  <div>
    <div class="flex items-center justify-between mb-3">
      <h4 class="font-medium text-sm text-gray-700 flex items-center gap-2">
        <Icon icon="mdi:paperclip" class="w-4 h-4" />
        {{ t('attachments') }}
        <span v-if="itemCount" class="text-xs text-gray-400 bg-gray-100 rounded-full px-1.5 py-0.5">{{ itemCount }}</span>
      </h4>
      <span class="text-xs text-gray-400">{{ t('maxSize') }}</span>
    </div>

    <!-- Saved attachments -->
    <ul v-if="attachments.length || uploads.length || pendingFiles.length" class="space-y-2 mb-3">
      <li
        v-for="attachment in attachments"
        :key="attachment.id"
        class="flex items-center gap-3 p-2 rounded-lg border border-gray-200 bg-white"
      >
        <button
          type="button"
          class="w-11 h-11 rounded-md overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0 border border-gray-100"
          @click="openAttachment(attachment)"
        >
          <img
            v-if="kindOf(attachment) === 'image'"
            :src="urlOf(attachment)"
            :alt="attachment.originalFilename"
            class="w-full h-full object-cover"
            loading="lazy"
          />
          <Icon v-else :icon="iconOf(attachment)" class="w-6 h-6 text-gray-500" />
        </button>
        <button type="button" class="flex-1 min-w-0 text-start" @click="openAttachment(attachment)">
          <p class="text-sm font-medium text-gray-800 truncate" :title="attachment.originalFilename">
            {{ attachment.originalFilename }}
          </p>
          <p class="text-xs text-gray-400 truncate">
            {{ formatFileSize(attachment.size) }}
            <template v-if="attachment.user?.name"> · {{ attachment.user.name }}</template>
            <template v-if="attachment.createdAt"> · {{ formatTime(attachment.createdAt) }}</template>
          </p>
        </button>
        <div class="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            class="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
            :title="t('download')"
            @click="downloadAttachment(attachment)"
          >
            <Icon icon="mdi:download-outline" class="w-5 h-5" />
          </button>
          <button
            v-if="canEdit"
            type="button"
            class="p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
            :title="t('delete')"
            @click="deleteAttachment(attachment)"
          >
            <Icon icon="mdi:trash-can-outline" class="w-5 h-5" />
          </button>
        </div>
      </li>

      <!-- In-flight uploads -->
      <li
        v-for="upload in uploads"
        :key="upload.uid"
        class="flex items-center gap-3 p-2 rounded-lg border border-dashed border-blue-200 bg-blue-50/40"
      >
        <div class="w-11 h-11 rounded-md bg-white flex items-center justify-center flex-shrink-0">
          <Icon icon="mdi:loading" class="w-5 h-5 text-blue-500 animate-spin" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm text-gray-700 truncate">{{ upload.name }}</p>
          <el-progress :percentage="upload.progress" :stroke-width="4" :show-text="false" class="mt-1" />
        </div>
      </li>

      <!-- Files queued while creating a task -->
      <li
        v-for="(file, idx) in pendingFiles"
        :key="file.name + idx"
        class="flex items-center gap-3 p-2 rounded-lg border border-dashed border-gray-300 bg-gray-50"
      >
        <div class="w-11 h-11 rounded-md bg-white flex items-center justify-center flex-shrink-0 border border-gray-100">
          <Icon :icon="TASK_ATTACHMENT_ICONS[getTaskAttachmentKind(file.name, file.type)]" class="w-6 h-6 text-gray-500" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm text-gray-700 truncate">{{ file.name }}</p>
          <p class="text-xs text-gray-400">{{ formatFileSize(file.size) }} · {{ t('uploadsOnSave') }}</p>
        </div>
        <button
          type="button"
          class="p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
          :title="t('remove')"
          @click="removePending(idx)"
        >
          <Icon icon="mdi:close" class="w-5 h-5" />
        </button>
      </li>
    </ul>

    <el-upload
      v-if="canEdit"
      drag
      multiple
      :auto-upload="false"
      :show-file-list="false"
      :accept="TASK_ATTACHMENT_ACCEPT"
      :on-change="onFileSelected"
      class="task-attachments-upload"
    >
      <div class="flex items-center justify-center gap-2 py-1 text-sm text-gray-500">
        <Icon icon="mdi:cloud-upload-outline" class="w-5 h-5" />
        <span>{{ t('dropOrClick') }}</span>
      </div>
    </el-upload>

    <p v-else-if="!attachments.length" class="text-xs text-gray-400 text-center py-2">{{ t('noAttachments') }}</p>

    <TaskAttachmentViewer
      v-model:index="viewerIndex"
      :attachments="attachments"
      :url-of="urlOf"
      @download="downloadAttachment"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Icon } from '@iconify/vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { UploadFile } from 'element-plus'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useHTTP } from '@/plugins/http'
import { useSettingsStore } from '@/stores/settingsStore'
import { TaskAttachment } from '@/models/hr/Task'
import TaskAttachmentViewer from './TaskAttachmentViewer.vue'
import {
  TASK_ATTACHMENT_ACCEPT,
  TASK_ATTACHMENT_ICONS,
  formatFileSize,
  getTaskAttachmentKind,
  isTaskAttachmentWithinLimit,
  taskAttachmentURL
} from '@/lib/taskAttachments'

dayjs.extend(relativeTime)

const props = withDefaults(defineProps<{
  taskID: string | null
  pendingFiles?: File[]
  canEdit?: boolean
}>(), {
  pendingFiles: () => [],
  canEdit: true
})

const emit = defineEmits<{
  (e: 'update:pendingFiles', files: File[]): void
  (e: 'count-changed', count: number): void
}>()

const { t } = useI18n({
  messages: {
    en: {
      attachments: 'Attachments',
      maxSize: 'Up to 20 MB per file',
      dropOrClick: 'Drop files here or tap to upload',
      noAttachments: 'No attachments',
      uploadsOnSave: 'uploads when the task is saved',
      download: 'Download',
      delete: 'Delete',
      remove: 'Remove',
      confirmDelete: 'Delete this attachment?',
      fileTooLarge: '"{name}" is larger than 20 MB',
      uploadFailed: 'Failed to upload "{name}"',
      uploaded: 'Attachment uploaded',
      downloadFailed: 'Failed to download the file'
    },
    ar: {
      attachments: 'المرفقات',
      maxSize: 'حتى 20 ميجابايت لكل ملف',
      dropOrClick: 'اسحب الملفات هنا أو اضغط للرفع',
      noAttachments: 'لا توجد مرفقات',
      uploadsOnSave: 'سيتم الرفع عند حفظ المهمة',
      download: 'تنزيل',
      delete: 'حذف',
      remove: 'إزالة',
      confirmDelete: 'هل تريد حذف هذا المرفق؟',
      fileTooLarge: 'حجم "{name}" أكبر من 20 ميجابايت',
      uploadFailed: 'فشل رفع "{name}"',
      uploaded: 'تم رفع المرفق',
      downloadFailed: 'فشل تنزيل الملف'
    }
  }
})

const http = useHTTP()
const settingsStore = useSettingsStore()

const attachments = ref<TaskAttachment[]>([])
const uploads = ref<{ uid: number; name: string; progress: number }[]>([])
const viewerIndex = ref<number | null>(null)

const itemCount = computed(() => attachments.value.length + props.pendingFiles.length)

const kindOf = (attachment: TaskAttachment) => getTaskAttachmentKind(attachment.originalFilename || attachment.filename, attachment.mimetype)
const iconOf = (attachment: TaskAttachment) => TASK_ATTACHMENT_ICONS[kindOf(attachment)]
const urlOf = (attachment: TaskAttachment) => taskAttachmentURL(settingsStore.storagePath, attachment.taskID || props.taskID || '', attachment.filename)
const formatTime = (date: string | Date) => dayjs(date).fromNow()


const loadAttachments = async (id: string) => {
  try {
    const data = await http.get<TaskAttachment[]>(`/hr/tasks/${id}/attachments`)
    attachments.value = data || []
  } catch (e) {
    console.error(e)
  }
}

watch(() => props.taskID, (id) => {
  attachments.value = []
  if (id) loadAttachments(id)
}, { immediate: true })

const sendFile = async (file: File, uid: number) => {
  if (!props.taskID) return
  const entry = { uid, name: file.name, progress: 0 }
  uploads.value.push(entry)
  try {
    const data = new FormData()
    data.set('attachment', file)
    const created = await http.put<TaskAttachment>(`/hr/tasks/${props.taskID}/attachments`, data, {
      onUploadProgress: (evt: { loaded: number; total?: number }) => {
        const tracked = uploads.value.find(u => u.uid === uid)
        if (tracked && evt.total) tracked.progress = Math.round((evt.loaded / evt.total) * 100)
      }
    })
    if (created) {
      attachments.value.push(created)
      emit('count-changed', attachments.value.length)
      ElMessage.success(t('uploaded'))
    }
  } catch (e: any) {
    ElMessage.error(e?.body || e?.title || t('uploadFailed', { name: file.name }))
  } finally {
    uploads.value = uploads.value.filter(u => u.uid !== uid)
  }
}

const onFileSelected = (uploadFile: UploadFile) => {
  const file = uploadFile.raw
  if (!file) return
  if (!isTaskAttachmentWithinLimit(file.size)) {
    ElMessage.error(t('fileTooLarge', { name: file.name }))
    return
  }
  if (props.taskID) {
    sendFile(file, uploadFile.uid)
  } else {
    emit('update:pendingFiles', [...props.pendingFiles, file])
  }
}

const removePending = (idx: number) => {
  emit('update:pendingFiles', props.pendingFiles.filter((_, i) => i !== idx))
}

const openAttachment = (attachment: TaskAttachment) => {
  viewerIndex.value = attachments.value.findIndex(a => a.id === attachment.id)
}

const downloadAttachment = async (attachment: TaskAttachment) => {
  try {
    const response = await fetch(urlOf(attachment))
    if (!response.ok) throw new Error(response.statusText)
    const blob = await response.blob()
    const objectURL = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectURL
    link.download = attachment.originalFilename || attachment.filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(objectURL)
  } catch (e) {
    console.error(e)
    ElMessage.error(t('downloadFailed'))
  }
}

const deleteAttachment = async (attachment: TaskAttachment) => {
  try {
    await ElMessageBox.confirm(t('confirmDelete'), '', { type: 'warning' })
  } catch {
    return
  }
  try {
    await http.delete(`/hr/tasks/attachments/${attachment.id}`)
    viewerIndex.value = null
    attachments.value = attachments.value.filter(a => a.id !== attachment.id)
    emit('count-changed', attachments.value.length)
  } catch (e: any) {
    ElMessage.error(e?.body || e?.title || String(e))
  }
}
</script>

<style scoped>
.task-attachments-upload :deep(.el-upload-dragger) {
  padding: 12px;
}
</style>
